import type React from "react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import {
  deleteUser,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";

import { auth } from "@/lib/firebase/client";
import { createLogger } from "@/lib/logger";
const authCtxLog = createLogger("context:auth");

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  deleteUserAccount: () => Promise<void>;
  unsubscribeListeners: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsubscribes, setUnsubscribes] = useState<(() => void)[]>([]);

  function handleUserChange(user: User | null) {
    setUser(user);
    authCtxLog.debug("User changed", user?.uid);
    if (user) {
      authCtxLog.info("User authenticated", user.email);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      handleUserChange(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    unsubscribeListeners();
    await firebaseSignOut(auth);
  };

  const deleteUserAccount = async () => {
    if (auth.currentUser) {
      unsubscribeListeners();
      await deleteUser(auth.currentUser);
      handleUserChange(null);
    }
  };

  const unsubscribeListeners = () => {
    for (const unsubscribe of unsubscribes) {
      unsubscribe();
    }
    setUnsubscribes([]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signOut,
        deleteUserAccount,
        unsubscribeListeners,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
